/**
 * UsagePricingPage.tsx — Usage pricing catalog management
 *
 * Two-level hierarchy:
 *   1. Pricing Catalogs — named bundles (e.g. "Starter Usage", "Enterprise Usage")
 *   2. Catalog Items — event type + credit cost specific to that catalog
 *
 * Usage plans reference a catalog to determine credit deductions per event type.
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import DataTable, { type Column } from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import BulkActionBar from '../components/BulkActionBar';
import SlidePanel from '../components/SlidePanel';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';

interface UsageCatalog {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

interface CatalogItem {
  id: string;
  catalog_id: string;
  event_type: string;
  credit_cost: number;
  pricing_mode: 'per_request' | 'per_token' | 'per_unit' | 'currency';
  request_price: string;
  input_price_per_million: string;
  output_price_per_million: string;
  unit_price: string;
  currency: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface MasterEventType {
  id: string;
  event_type: string;
  display_name: string;
  unit: string;
  credit_cost: number;
  is_active: boolean;
}

function useCatalogs() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<UsageCatalog[]>({
    queryKey: ['usage-catalogs'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load catalogs: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function useCreateCatalog() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Create failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalogs'] }),
  });
}

function useUpdateCatalog() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; description?: string; is_active?: boolean }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalogs'] }),
  });
}

function useDeleteCatalog() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalogs'] }),
  });
}

function useBulkDeleteCatalogs() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`Bulk delete failed: ${res.status}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalogs'] }),
  });
}

function useCatalogItems(catalogId: string | null) {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<CatalogItem[]>({
    queryKey: ['usage-catalog-items', catalogId],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${catalogId}/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load items: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token && !!catalogId,
  });
}

function useAddCatalogItem(catalogId: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { event_type: string; pricing_mode: string; request_price?: string; input_price_per_million?: string; output_price_per_million?: string; unit_price?: string; currency?: string; description?: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${catalogId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Add item failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalog-items', catalogId] }),
  });
}

function useUpdateCatalogItem(catalogId: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, ...data }: { itemId: string; pricing_mode?: string; request_price?: string; input_price_per_million?: string; output_price_per_million?: string; unit_price?: string; currency?: string; description?: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${catalogId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalog-items', catalogId] }),
  });
}

function useDeleteCatalogItem(catalogId: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs/${catalogId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['usage-catalog-items', catalogId] }),
  });
}

function useMasterEventTypes() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<MasterEventType[]>({
    queryKey: ['admin-usage-event-types'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-event-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function PricingBadge({ mode }: { mode: string }) {
  const colors: Record<string, string> = {
    per_token: 'bg-violet-100 text-violet-800',
    per_request: 'bg-cyan-100 text-cyan-800',
    per_unit: 'bg-amber-100 text-amber-800',
  };
  const labels: Record<string, string> = {
    per_token: 'Per Token',
    per_request: 'Per Request',
    per_unit: 'Per Unit',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${colors[mode] ?? 'bg-slate-100 text-slate-600'}`}>
      {labels[mode] ?? mode}
    </span>
  );
}

function PricingFormFields({
  pricingMode, setPricingMode,
  requestPrice, setRequestPrice,
  inputPrice, setInputPrice,
  outputPrice, setOutputPrice,
  unitPrice, setUnitPrice,
  unitLabel,
}: {
  pricingMode: string; setPricingMode: (v: string) => void;
  requestPrice: string; setRequestPrice: (v: string) => void;
  inputPrice: string; setInputPrice: (v: string) => void;
  outputPrice: string; setOutputPrice: (v: string) => void;
  unitPrice: string; setUnitPrice: (v: string) => void;
  unitLabel: string;
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700">Pricing Mode</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {(['per_token', 'per_request', 'per_unit', 'currency'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setPricingMode(mode)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                pricingMode === mode
                  ? 'border-cyan-400 bg-cyan-50 text-cyan-900'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {mode === 'per_token' ? '🪙 Per Token' : mode === 'per_request' ? '📨 Per Request' : mode === 'per_unit' ? '📊 Per Unit' : '💲 Currency (cents)'}
            </button>
          ))}
        </div>
      </div>
      {pricingMode === 'per_token' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">Input / 1M tokens</label>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-sm text-slate-400">$</span>
              <input type="number" step="0.01" value={inputPrice} onChange={(e) => setInputPrice(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Output / 1M tokens</label>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-sm text-slate-400">$</span>
              <input type="number" step="0.01" value={outputPrice} onChange={(e) => setOutputPrice(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
        </div>
      )}
      {pricingMode === 'per_request' && (
        <div>
          <label className="block text-xs font-medium text-slate-600">Price per request</label>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-sm text-slate-400">$</span>
            <input type="number" step="0.001" value={requestPrice} onChange={(e) => setRequestPrice(e.target.value)} className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            <span className="text-xs text-slate-400">/ request</span>
          </div>
        </div>
      )}
      {pricingMode === 'per_unit' && (
        <div>
          <label className="block text-xs font-medium text-slate-600">Price per {unitLabel}</label>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-sm text-slate-400">$</span>
            <input type="number" step="0.001" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            <span className="text-xs text-slate-400">/ {unitLabel}</span>
          </div>
        </div>
      )}
      {pricingMode === 'currency' && (
        <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 p-3">
          <p className="text-xs text-cyan-800">
            <strong>💲 Currency mode:</strong> The event's <code className="rounded bg-cyan-100 px-1">quantity</code> field IS the cost in cents.
            No price field needed — the billing system burns exactly <code className="rounded bg-cyan-100 px-1">quantity</code> cents from the credit balance.
            Used for <code className="rounded bg-cyan-100 px-1">ai_invocation</code> events where the marked-up cost is already calculated.
          </p>
        </div>
      )}
    </div>
  );
}

function CatalogDetailView({ catalog, onBack }: { catalog: UsageCatalog; onBack: () => void }): React.JSX.Element {
  const { data: items, isLoading } = useCatalogItems(catalog.id);
  const { data: masterTypes } = useMasterEventTypes();
  const addItem = useAddCatalogItem(catalog.id);
  const updateItem = useUpdateCatalogItem(catalog.id);
  const deleteItem = useDeleteCatalogItem(catalog.id);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CatalogItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CatalogItem | null>(null);
  const [showIntegration, setShowIntegration] = useState(false);

  // Add form state
  const [addEventType, setAddEventType] = useState('');
  const [addPricingMode, setAddPricingMode] = useState('per_request');
  const [addRequestPrice, setAddRequestPrice] = useState('0');
  const [addInputPrice, setAddInputPrice] = useState('0');
  const [addOutputPrice, setAddOutputPrice] = useState('0');
  const [addUnitPrice, setAddUnitPrice] = useState('0');
  const [addDescription, setAddDescription] = useState('');

  // Edit form state
  const [editPricingMode, setEditPricingMode] = useState('per_request');
  const [editRequestPrice, setEditRequestPrice] = useState('0');
  const [editInputPrice, setEditInputPrice] = useState('0');
  const [editOutputPrice, setEditOutputPrice] = useState('0');
  const [editUnitPrice, setEditUnitPrice] = useState('0');
  const [editDescription, setEditDescription] = useState('');

  const usedEventTypes = new Set((items ?? []).map((i) => i.event_type));
  const availableTypes = (masterTypes ?? []).filter((t) => t.is_active && !usedEventTypes.has(t.event_type));

  function openEdit(item: CatalogItem) {
    setEditTarget(item);
    setEditPricingMode(item.pricing_mode || 'per_request');
    setEditRequestPrice(item.request_price || '0');
    setEditInputPrice(item.input_price_per_million || '0');
    setEditOutputPrice(item.output_price_per_million || '0');
    setEditUnitPrice(item.unit_price || '0');
    setEditDescription(item.description ?? '');
  }

  async function handleAddItem(): Promise<void> {
    if (!addEventType) return;
    await addItem.mutateAsync({
      event_type: addEventType,
      pricing_mode: addPricingMode,
      request_price: addRequestPrice,
      input_price_per_million: addInputPrice,
      output_price_per_million: addOutputPrice,
      unit_price: addUnitPrice,
      description: addDescription || undefined,
    });
    setAddOpen(false); setAddEventType(''); setAddPricingMode('per_request');
    setAddRequestPrice('0'); setAddInputPrice('0'); setAddOutputPrice('0'); setAddUnitPrice('0'); setAddDescription('');
  }

  async function handleUpdateItem(): Promise<void> {
    if (!editTarget) return;
    await updateItem.mutateAsync({
      itemId: editTarget.id,
      pricing_mode: editPricingMode,
      request_price: editRequestPrice,
      input_price_per_million: editInputPrice,
      output_price_per_million: editOutputPrice,
      unit_price: editUnitPrice,
      description: editDescription || undefined,
    });
    setEditTarget(null);
  }

  async function handleDeleteItem(): Promise<void> {
    if (!deleteTarget) return;
    await deleteItem.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }

  function formatPrice(item: CatalogItem): string {
    switch (item.pricing_mode) {
      case 'per_token': return `$${Number(item.input_price_per_million).toFixed(2)} in / $${Number(item.output_price_per_million).toFixed(2)} out per 1M`;
      case 'per_request': return `$${Number(item.request_price).toFixed(4)} / req`;
      case 'per_unit': return `$${Number(item.unit_price).toFixed(4)} / unit`;
      default: return '—';
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setAddOpen(true)} disabled={availableTypes.length === 0} className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-600 disabled:opacity-40">+ Add</button>
          <button onClick={() => setShowIntegration(!showIntegration)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            {showIntegration ? 'Hide' : '📡'} Integration Guide
          </button>
        </div>
        <span className="text-xs text-slate-400">{(items ?? []).length} event types</span>
      </div>

      {/* Integration Guidance (collapsible) */}
      {showIntegration && (
        <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50 to-cyan-50 px-5 py-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-800">Integration Guide</h4>
          <p className="mt-1 text-xs text-blue-700">Report usage events via the billing API. Each event must include the event type key, quantity, and optional metadata for per-token pricing.</p>
          <div className="mt-3 rounded-lg bg-white/80 p-3 font-mono text-[11px] text-slate-700">
            <p className="text-slate-500">POST /usage/events</p>
            <pre className="mt-1 whitespace-pre-wrap">{`{
  "events": [{
    "customer_id": "<org-uuid>",
    "event_type": "ai_completion",
    "quantity": 6000,
    "idempotency_key": "<org>:<correlation_id>:RCV",
    "properties": {
      "input_tokens": 4200,
      "output_tokens": 1800,
      "model": "claude-sonnet-4-20250514",
      "correlation_id": "<uuid>"
    },
    "occurredAt": "2026-06-08T12:00:00Z"
  }]
}`}</pre>
          </div>
          <div className="mt-3 space-y-1.5 text-[11px] text-blue-700">
            <p><strong>Auth:</strong> <code className="rounded bg-white/60 px-1">Authorization: Bearer &lt;BILLING_SERVICE_TOKEN&gt;</code></p>
            <p><strong>Idempotency:</strong> Always include <code className="rounded bg-white/60 px-1">idempotency_key</code> — duplicate keys are silently ignored (exactly-once delivery).</p>
            <p><strong>Per-token pricing:</strong> Include <code className="rounded bg-white/60 px-1">input_tokens</code> and <code className="rounded bg-white/60 px-1">output_tokens</code> in properties. Set quantity = input_tokens + output_tokens.</p>
            <p><strong>Per-request pricing:</strong> Set <code className="rounded bg-white/60 px-1">quantity: 1</code>. Pricing uses the flat request_price from the catalog.</p>
            <p><strong>Metadata validation:</strong> If the event type defines <code className="rounded bg-white/60 px-1">required_metadata_schema</code>, properties are validated against it. Invalid payloads return 422.</p>
            <p><strong>Billing modes:</strong> Usage charges are deducted from <em>credits</em> (real-time) or accumulated for <em>arrears</em> (billed at period end), based on the plan's pricing model config.</p>
          </div>
          <div className="mt-3 rounded-lg bg-red-50/80 p-3 text-[11px] text-red-700">
            <p className="font-semibold">Error Responses</p>
            <p className="mt-1"><code>422</code> — Event type has required metadata schema and properties don't match. Response includes the full JSON Schema and an example payload.</p>
            <p><code>404</code> — Unknown event_type key. Verify the key matches a registered event type.</p>
            <p><code>402</code> — Insufficient credits for credit-based billing. Response includes current balance.</p>
          </div>
        </div>
      )}

      {/* Compact Event List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <p className="px-5 py-6 text-sm text-slate-400">Loading...</p>}
        {!isLoading && (items ?? []).length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-slate-400">No event types priced yet. Click "+ Add" to configure pricing.</p>
        )}
        {(items ?? []).map((item) => {
          const master = masterTypes?.find((m) => m.event_type === item.event_type);
          return (
            <div
              key={item.id}
              onClick={() => openEdit(item)}
              className="group relative flex cursor-pointer items-center gap-3 border-b border-slate-100 px-5 py-3 transition hover:bg-slate-50 active:bg-slate-100"
            >
              {/* Left: name + key */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{master?.display_name ?? item.event_type}</p>
                <p className="truncate font-mono text-[10px] text-slate-400">{item.event_type}</p>
                {item.description && (
                  <p className="mt-0.5 truncate text-[11px] text-slate-500">{item.description}</p>
                )}
              </div>
              {/* Middle: pricing badge */}
              <PricingBadge mode={item.pricing_mode} />
              {/* Right: rate */}
              <span className="w-[140px] text-right font-mono text-xs tabular-nums text-slate-600">{formatPrice(item)}</span>
              {/* Delete: show on hover (desktop), swipe reveal on mobile */}
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-red-50 px-2 py-1 text-[10px] font-medium text-red-600 opacity-0 transition group-hover:opacity-100 sm:static sm:translate-y-0"
                title="Remove"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      {/* Add Modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Add Event Type Pricing</h3>
            <p className="mt-1 text-sm text-slate-500">Select an event type and configure how it's priced in this catalog.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Event Type</label>
                <select value={addEventType} onChange={(e) => setAddEventType(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {availableTypes.map((t) => <option key={t.event_type} value={t.event_type}>{t.display_name} ({t.event_type})</option>)}
                </select>
              </div>
              {addEventType && (
                <>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Description</label>
                  <textarea
                    value={addDescription}
                    onChange={(e) => setAddDescription(e.target.value)}
                    rows={2}
                    placeholder="e.g. Hard AI cost paid to the model provider"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Shown on reports and bills to explain this charge.</p>
                </div>
                <PricingFormFields
                  pricingMode={addPricingMode} setPricingMode={setAddPricingMode}
                  requestPrice={addRequestPrice} setRequestPrice={setAddRequestPrice}
                  inputPrice={addInputPrice} setInputPrice={setAddInputPrice}
                  outputPrice={addOutputPrice} setOutputPrice={setAddOutputPrice}
                  unitPrice={addUnitPrice} setUnitPrice={setAddUnitPrice}
                  unitLabel={masterTypes?.find((t) => t.event_type === addEventType)?.unit ?? 'unit'}
                />
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setAddOpen(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={() => void handleAddItem()} disabled={!addEventType || addItem.isPending} className="rounded-md bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50">{addItem.isPending ? 'Adding…' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900">{masterTypes?.find((m) => m.event_type === editTarget.event_type)?.display_name ?? editTarget.event_type}</h3>
            <p className="mt-0.5 font-mono text-[11px] text-slate-400">{editTarget.event_type}</p>
            {editTarget.description && (
              <p className="mt-1 text-xs text-slate-500">{editTarget.description}</p>
            )}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={2}
                placeholder="e.g. Platform fee used to provide rule42 services"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-slate-400">Shown on reports and bills to explain this charge.</p>
            </div>
            <div className="mt-4">
              <PricingFormFields
                pricingMode={editPricingMode} setPricingMode={setEditPricingMode}
                requestPrice={editRequestPrice} setRequestPrice={setEditRequestPrice}
                inputPrice={editInputPrice} setInputPrice={setEditInputPrice}
                outputPrice={editOutputPrice} setOutputPrice={setEditOutputPrice}
                unitPrice={editUnitPrice} setUnitPrice={setEditUnitPrice}
                unitLabel={masterTypes?.find((t) => t.event_type === editTarget.event_type)?.unit ?? 'unit'}
              />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditTarget(null)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={() => void handleUpdateItem()} disabled={updateItem.isPending} className="rounded-md bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50">{updateItem.isPending ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={deleteTarget !== null} title="Remove Event Type" message={`Remove "${deleteTarget?.event_type}" from this catalog?`} confirmLabel="Remove" cancelLabel="Cancel" onConfirm={() => void handleDeleteItem()} onCancel={() => setDeleteTarget(null)} loading={deleteItem.isPending} />
    </div>
  );
}

function CatalogForm({ initial, onSubmit, onCancel, loading }: { initial?: UsageCatalog | null; onSubmit: (d: { name: string; description: string }) => void; onCancel: () => void; loading?: boolean }): React.JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">{initial ? 'Edit Catalog' : 'New Pricing Catalog'}</h3>
      <div>
        <label className="block text-sm font-medium text-slate-700">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Enterprise Usage" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700">Description</label>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Optional…" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
        <button onClick={() => onSubmit({ name, description: desc })} disabled={loading || !name.trim()} className="rounded-md bg-cyan-500 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50">{loading ? 'Saving…' : initial ? 'Update' : 'Create'}</button>
      </div>
    </div>
  );
}

export default function UsagePricingPage(): React.JSX.Element {
  const { data: catalogs, isLoading, error } = useCatalogs();
  const createCatalog = useCreateCatalog();
  const updateCatalog = useUpdateCatalog();
  const deleteCatalog = useDeleteCatalog();
  const bulkDelete = useBulkDeleteCatalogs();

  const [activeCatalog, setActiveCatalog] = useState<UsageCatalog | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<UsageCatalog | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UsageCatalog | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  async function handleCreate(data: { name: string; description: string }): Promise<void> {
    await createCatalog.mutateAsync(data); setShowForm(false);
  }
  async function handleUpdate(data: { name: string; description: string }): Promise<void> {
    if (!editTarget) return;
    await updateCatalog.mutateAsync({ id: editTarget.id, ...data }); setEditTarget(null);
  }
  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    await deleteCatalog.mutateAsync(deleteTarget.id); setDeleteTarget(null);
  }
  async function handleBulkDelete(): Promise<void> {
    setBulkDeleting(true);
    try { await bulkDelete.mutateAsync([...selected]); setSelected(new Set()); setBulkDeleteOpen(false); }
    finally { setBulkDeleting(false); }
  }

  const columns: Column<UsageCatalog>[] = [
    {
      key: 'name', header: 'Catalog',
      render: (row) => (
        <div>
          <button onClick={() => setActiveCatalog(row)} className="text-left font-medium text-slate-900 hover:text-cyan-700 hover:underline">{row.name}</button>
          {row.description && <p className="mt-0.5 text-xs text-slate-500">{row.description}</p>}
        </div>
      ),
    },
    { key: 'item_count', header: 'Event Types', render: (row) => <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{row.item_count}</span> },
    { key: 'is_active', header: 'Status', render: (row) => <ActiveBadge active={row.is_active} /> },
    {
      key: 'actions', header: '', sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => setActiveCatalog(row)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">Open</button>
          <button onClick={() => setEditTarget(row)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50">Edit</button>
          <button onClick={() => setDeleteTarget(row)} className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">Delete</button>
        </div>
      ),
    },
  ];

  if (false) {
    // Previously used for full-page catalog detail — now replaced by SlidePanel
    return <AdminLayout title="Usage Pricing"><CatalogDetailView catalog={activeCatalog!} onBack={() => setActiveCatalog(null)} /></AdminLayout>;
  }

  return (
    <AdminLayout title="Usage Pricing">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Usage-based billing</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Pricing Catalogs</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Create named pricing catalogs with their own credit costs per event type. Assign a catalog to a usage-based plan — subscribers get billed at that catalog&apos;s rates.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-center">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Catalogs</p>
                <p className="text-xl font-semibold text-slate-900">{(catalogs ?? []).length}</p>
              </div>
              <button onClick={() => setShowForm(true)} className="rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300">New Catalog</button>
            </div>
          </div>
        </section>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{(error as Error).message}</div>}

        {selected.size > 0 && <BulkActionBar count={selected.size} label="catalog" onDelete={() => setBulkDeleteOpen(true)} onClear={() => setSelected(new Set())} />}

        <DataTable<UsageCatalog> columns={columns} rows={catalogs ?? []} rowKey={(r) => r.id} loading={isLoading} emptyMessage="No pricing catalogs yet. Create your first catalog." selectable selectedKeys={selected} onSelectionChange={setSelected} onRowClick={(row) => setActiveCatalog(row)} />
      </div>

      {/* Create/Edit slide panel */}
      <SlidePanel
        open={showForm || editTarget !== null}
        onClose={() => { setShowForm(false); setEditTarget(null); }}
        size="sm"
        title={editTarget ? 'Edit Catalog' : 'New Pricing Catalog'}
        category="Usage Pricing"
      >
        <div className="px-6 py-6">
          <CatalogForm initial={editTarget} onSubmit={(d) => { if (editTarget) void handleUpdate(d); else void handleCreate(d); }} onCancel={() => { setShowForm(false); setEditTarget(null); }} loading={createCatalog.isPending || updateCatalog.isPending} />
        </div>
      </SlidePanel>

      {/* Catalog detail slide panel */}
      <SlidePanel
        open={activeCatalog !== null}
        onClose={() => setActiveCatalog(null)}
        size="lg"
        title={activeCatalog?.name}
        subtitle={activeCatalog?.description ?? undefined}
        category="Catalog Event Types"
      >
        {activeCatalog && <CatalogDetailView catalog={activeCatalog} onBack={() => setActiveCatalog(null)} />}
      </SlidePanel>

      <ConfirmDialog open={deleteTarget !== null} title="Delete Catalog" message={<div><p>Delete <strong>"{deleteTarget?.name}"</strong>?</p><p className="mt-2 text-red-600 font-medium">All event type pricing and plan references will be removed.</p></div>} confirmLabel="Delete" cancelLabel="Cancel" onConfirm={() => void handleDelete()} onCancel={() => setDeleteTarget(null)} loading={deleteCatalog.isPending} />
      <ConfirmDialog open={bulkDeleteOpen} title={`Delete ${selected.size} Catalog${selected.size !== 1 ? 's' : ''}`} message={<div><p>Delete <strong>{selected.size}</strong> catalog{selected.size !== 1 ? 's' : ''}?</p><p className="mt-2 text-red-600 font-medium">This cannot be undone.</p></div>} confirmLabel="Delete All" cancelLabel="Cancel" onConfirm={() => void handleBulkDelete()} onCancel={() => setBulkDeleteOpen(false)} loading={bulkDeleting} />
    </AdminLayout>
  );
}
